import type { JobStatus } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { Empty, ErrorBox, PageHead, QueryView } from '../components/ui';
import { formatQty, whenAt } from '../format';
import { useLocale } from '../i18n';
import type { AppMessageKey } from '../messages';
import { qk } from '../queryClient';

/** The app about itself, for the owner: version, database, errors, who signs in, what is switched on. */

/** decision 153: a job that ran with nothing to do, or did not run when it should have, is not shown as OK */
const STATE_BADGE: Record<JobStatus['state'], string> = { ok: 'badge badge-ok', idle: 'badge badge-warn', late: 'badge badge-warn', failed: 'badge badge-danger', never: 'badge' };
const STATE_LABEL: Record<JobStatus['state'], AppMessageKey> = { ok: 'jobOk', idle: 'jobIdle', late: 'jobLate', failed: 'jobFailed', never: 'jobNeverRan' };

export function SystemPage() {
  const { t, intlLocale } = useLocale();
  // a job without a label still shows its name
  const jobLabel = (name: string) => {
    const key = `job_${name}` as 'job_calendar';
    const label = t(key);
    return label && label !== key ? label : name;
  };
  const health = useQuery({ queryKey: qk.systemHealth, queryFn: ({ signal }) => api.systemHealth(signal) });
  const [open, setOpen] = useState<number | null>(null);
  const onOff = (v: boolean) => (v ? t('switchedOn') : t('switchedOffState'));
  const qc = useQueryClient();
  const jobs = useQuery({ queryKey: qk.systemJobs, queryFn: ({ signal }) => api.systemJobs(signal) });
  const run = useMutation({
    mutationFn: (name: string) => api.runJob(name),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.systemJobs });
      void qc.invalidateQueries({ queryKey: qk.signals });
    },
  });
  return (
    <div className="stack">
      <PageHead title={t('systemTitle')} back="/" />
      <p className="muted">{t('systemHint')}</p>
      <QueryView query={health}>
        {(h) => (
          <>
            <section className="card">
              <dl className="facts plain">
                <div className="fact">
                  <dt>{t('appVersion')}</dt>
                  <dd>{h.version ? h.version.slice(0, 10) : t('devBuild')}</dd>
                </div>
                <div className="fact">
                  <dt>{t('startedAt')}</dt>
                  <dd>{whenAt(h.startedAt, intlLocale)}</dd>
                </div>
                <div className="fact">
                  <dt>{t('database')}</dt>
                  <dd data-testid="db-fact">{h.database.ok ? `${h.database.sizeMb} MB · ${h.database.migrations} ${t('migrations')}` : t('dbDown')}</dd>
                </div>
                <div className="fact">
                  <dt>{t('people')}</dt>
                  <dd>{h.users.active} · {h.users.withPassword} {t('withPassword')} · {h.users.withPasskey} {t('withPasskey')} · {h.users.signedInLast7d} {t('seenThisWeek')}</dd>
                </div>
                <div className="fact">
                  <dt>{t('switches')}</dt>
                  <dd>{t('storage')} {onOff(h.storage.configured)} · {t('readerShort')} {onOff(h.reader)} · {t('textingShort')} {onOff(h.texting)}</dd>
                </div>
              </dl>
            </section>
            <section className="card stack-sm">
              <h2>
                {t('errorsTitle')} <span className="label">{h.errors.last24h} / 24h · {h.errors.last7d} / 7d</span>
              </h2>
              {h.errors.recent.length === 0 ? (
                <Empty>{t('noErrors')}</Empty>
              ) : (
                <ul className="list plain" data-testid="errors">
                  {h.errors.recent.map((e) => (
                    <li key={e.id} className="stack-sm">
                      <button type="button" className="row" onClick={() => setOpen(open === e.id ? null : e.id)} aria-expanded={open === e.id}>
                        <span className="row-main">
                          <span className="row-title">
                            <span className={e.side === 'api' ? 'badge badge-danger' : 'badge badge-warn'}>{e.side === 'api' ? t('server') : t('browser')}</span> {e.message}
                          </span>
                          <span className="row-sub">
                            {whenAt(e.at, intlLocale)}
                            {e.path ? ` · ${e.method ? `${e.method} ` : ''}${e.path}` : ''}
                            {e.userName ? ` · ${e.userName}` : ''}
                            {e.requestId ? ` · ${e.requestId}` : ''}
                          </span>
                        </span>
                      </button>
                      {open === e.id && e.detail ? <pre className="muted" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{e.detail}</pre> : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </QueryView>
      <QueryView query={jobs}>
        {(j) => (
          <section className="card stack-sm" data-testid="jobs">
            <h2>
              {t('jobsTitle')} <span className="label">{t('jobsRunAt').replace('{hour}', String(j.runAtHour))}</span>
            </h2>
            <p className="muted">{t('jobsHint')}</p>
            <ul className="list plain">
              {j.jobs.map((job) => (
                <li key={job.name} className="row">
                  <span className="row-main">
                    <span className="row-title">
                      <span className={STATE_BADGE[job.state]}>{t(STATE_LABEL[job.state])}</span> {jobLabel(job.name)}
                    </span>
                    <span className="row-sub">
                      {job.last ? `${whenAt(job.last.finishedAt, intlLocale)} · ${job.last.rows} ${t('jobRows')}${job.last.detail ? ` · ${job.last.detail}` : ''}` : job.summary}
                    </span>
                  </span>
                  <button type="button" className="btn btn-secondary" disabled={run.isPending} onClick={() => run.mutate(job.name)}>
                    {t('runNow')}
                  </button>
                </li>
              ))}
            </ul>
            {run.error ? <ErrorBox error={run.error} /> : null}
          </section>
        )}
      </QueryView>
      <ForecastScore />
    </div>
  );
}

/** How close the plain forecast came over the last 28 days (decision 58), next to the jobs that score it. */
function ForecastScore() {
  const { t } = useLocale();
  const q = useQuery({ queryKey: qk.signals, queryFn: ({ signal }) => api.signals(signal), staleTime: 5 * 60_000 });
  if (q.isError) return <ErrorBox error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return null;
  const f = q.data.forecast;
  return (
    <section className="card stack-sm" data-testid="forecast-score">
      <h2>{t('forecastScoreTitle')}</h2>
      {f.scoredDays === 0 ? (
        <Empty>{t('forecastNotScored')}</Empty>
      ) : (
        <>
          <dl className="facts plain">
            <div className="fact">
              <dt>{t('forecastWeekdayAvg')}</dt>
              <dd className="money">{f.accuracy.weekdayAvg === null ? '—' : `${f.accuracy.weekdayAvg}%`}</dd>
            </div>
            <div className="fact">
              <dt>{t('forecastLastWeek')}</dt>
              <dd>{f.accuracy.lastWeek === null ? '—' : `${f.accuracy.lastWeek}%`}</dd>
            </div>
          </dl>
          <p className="muted">{t('forecastScoreHint').replace('{days}', String(f.days))}</p>
          {f.worst.length ? (
            <ul className="list">
              {f.worst.map((w) => (
                <li key={w.itemId} className="row">
                  <span className="row-main">
                    <span className="row-title">{w.itemName}</span>
                    <span className="row-sub">
                      {t('forecastSaid')} {formatQty(w.forecast)} · {t('soldWas')} {formatQty(w.actual)}
                    </span>
                  </span>
                  <span className={w.offBy > 0 ? 'row-end-value owed' : 'row-end-value'}>{w.offBy > 0 ? '+' : ''}{formatQty(w.offBy)}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}
    </section>
  );
}
